import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import { fmtDate } from "../format";
import { useToast } from "../toast";
import type { ApiKey } from "../types";

export default function ApiKeys() {
  const authApi = useAuthApi();
  const toast = useToast();

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await authApi<ApiKey[]>(`${MGMT}/api-keys`);
      setKeys(data ?? []);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [authApi, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const data = await authApi<{ key: string } & ApiKey>(`${MGMT}/api-keys`, {
        method: "POST",
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (data) {
        setCreatedKey(data.key);
        await load();
      }
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setCreating(false);
      setNewName("");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await authApi(`${MGMT}/api-keys/${id}`, { method: "DELETE" });
      toast.show("API key revoked");
      await load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.show("Copied to clipboard");
  };

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Generate API Key
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        API keys can be used to authenticate programmatic API calls using the{" "}
        <code>Authorization: Bearer &lt;key&gt;</code> header.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Key Prefix</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && keys.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No API keys yet. Generate one to get started.
                </TableCell>
              </TableRow>
            )}
            {keys.map((k) => (
              <TableRow key={k.id}>
                <TableCell>{k.name}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{k.key_prefix}…</TableCell>
                <TableCell>{fmtDate(k.created_at)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Revoke key">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(k.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Key name"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            placeholder="e.g. CI pipeline, Home Assistant"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
          >
            {creating ? <CircularProgress size={18} /> : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Show newly created key */}
      <Dialog
        open={createdKey !== null}
        onClose={() => { setCreatedKey(null); setCreateOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>API Key Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now — it will not be shown again.
          </Alert>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              sx={{
                fontFamily: "monospace",
                bgcolor: "action.hover",
                p: 1.5,
                borderRadius: 1,
                flexGrow: 1,
                wordBreak: "break-all",
              }}
            >
              {createdKey}
            </Typography>
            <Tooltip title="Copy">
              <IconButton onClick={() => createdKey && copyToClipboard(createdKey)}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => { setCreatedKey(null); setCreateOpen(false); }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
